/* eslint-disable no-plusplus */
import {
  CaseDetails,
  CaseDetailsResponsePayloadDTO,
  CaseNote,
  EconsultManagedSpecialtyProgram,
  EconsultSpecialist,
  EconsultStatusTypes,
  EconsultUserRoles,
  MirthResponse,
  NoteActionTypes,
  NoteStatusCodes,
  NoteTypes,
  UserDTO
} from '@oh-vcp/components-common';
import { AxiosProgressEvent } from 'axios';
import apiService from './apiService';
import EconsultUserService from './econsultUserService';

const CaseService = {
  redirectCaseDetails: async (caseDetails: CaseDetails[]): Promise<any> => {
    const url = `cases/redirect`;
    const response = await apiService.post(url, caseDetails);
    return response.data;
  },
  upsertCaseDetails: async (caseDetails: CaseDetails): Promise<CaseDetailsResponsePayloadDTO> => {
    const url = `cases/`;
    const response = await apiService.post(url, caseDetails);
    return response.data;
  },
  upsertCaseNote: async (caseNote: CaseNote): Promise<CaseNote> => {
    const response = await apiService.post('cases/note', caseNote);
    return response.data?.data;
  },
  fetchCaseSpecialistsAndGroups: async (
    searchTerm: string
  ): Promise<(UserDTO | EconsultManagedSpecialtyProgram)[]> => {
    const url = `search/econsult/specialistsandprograms/list?q=${searchTerm}`;
    const response = await apiService.get(url);
    return response.data;
  },
  getBulkReportEentriesnumber: async (
    reportAsType: string,
    startDate: string,
    endDate: string,
    delegatorId: string
  ): Promise<any> => {
    let url = `cases/export/pdf/bulk/entriesnumber?mode=${reportAsType}&startDate=${startDate}&endDate=${endDate}`;
    if (delegatorId && delegatorId !== '0') {
      url += `&delegatorId=${encodeURIComponent(delegatorId)}`;
    }
    const response = await apiService.get(url);
    return response.data;
  },
  getProgramSpecialists: async (programId: string): Promise<EconsultSpecialist[]> => {
    const response = await apiService.get(`programs/${programId}/specialists`);
    return response.data;
  },
  uploadFile: async (
    file: File,
    progressCallback: (progress: number) => void,
    signal?: AbortSignal
  ): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const metadataResponse = await apiService.post('hubapidocuments/documents/', {
        fileName: file.name
      });
      const { url } = metadataResponse.data;
      const response = await apiService.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent && progressEvent.total) {
            const progress = (progressEvent.loaded / progressEvent.total) * 100;
            progressCallback(progress);
          }
        },
        signal
      });
      return response.data;
    } catch (error) {
      console.error('Error during file upload:', error);
      throw error;
    }
  },
  getFileUrl: async (fileId: string): Promise<string> => {
    const response = await apiService.get(`hubapidocuments/documents/${fileId}`);
    if (response && response.data) {
      return response.data.url;
    }
    return '';
  },
  getLastActiveNote: (caseDetails: CaseDetails): CaseNote | null => {
    const sortedNotes = caseDetails.notes?.sort((a: CaseNote, b: CaseNote) =>
      !a.lastUpdated || !b.lastUpdated || a.lastUpdated > b.lastUpdated ? -1 : 1
    );
    let lastActiveNote: CaseNote | null = null;
    sortedNotes?.forEach((currentNote) => {
      if (
        lastActiveNote === null &&
        currentNote.statusCd === NoteStatusCodes.active &&
        currentNote.actionTypeCd !== NoteActionTypes.addNote
      ) {
        lastActiveNote = currentNote;
      }
    });
    return lastActiveNote;
  },
  isCaseCompleted: (caseDetails: CaseDetails | undefined): boolean => {
    if (!caseDetails) {
      return false;
    }
    if (caseDetails.statusCd === NoteStatusCodes.closed) {
      return true;
    }
    const sortedNotes = caseDetails.notes;
    let finishedActiveNote: CaseNote | null = null;
    sortedNotes?.forEach((currentNote) => {
      if (
        finishedActiveNote === null &&
        currentNote.statusCd === NoteStatusCodes.active &&
        (currentNote.actionTypeCd === NoteActionTypes.finish ||
          currentNote.actionTypeCd === NoteActionTypes.cancel)
      ) {
        finishedActiveNote = currentNote;
      }
    });
    return finishedActiveNote !== null;
  },
  containsUnassignNote: (caseDetails: CaseDetails) => {
    let contains = false;
    const { notes } = caseDetails;

    if (!notes || notes.length === 0) return false;

    for (let i = 0; i < notes.length; i++) {
      const currentNote = notes[i];
      if (
        currentNote.statusCd === NoteStatusCodes.active &&
        currentNote.actionTypeCd === NoteActionTypes.unassign
      ) {
        contains = true;
        break;
      }
    }
    return contains;
  },
  getLastNoteWithRole: (caseDetails: CaseDetails, userRole: string) => {
    const caseNotes = caseDetails?.notes;
    if (!caseNotes || caseNotes.length === 0) {
      return null;
    }

    if (userRole === EconsultUserRoles.consultantDelegate) {
      userRole = EconsultUserRoles.consultant;
    } else if (userRole === EconsultUserRoles.referrerDelegate) {
      userRole = EconsultUserRoles.referrer;
    }

    const caseNotesForRole = caseNotes
      .filter((n) => n.noteTypeCd === userRole)
      .sort((a, b) => (b.lastUpdated ?? 0) - (a.lastUpdated ?? 0));
    return caseNotesForRole;
  },
  containsConsultProvidedNote: (caseDetails: CaseDetails) => {
    let contains = false;
    const { notes } = caseDetails;
    notes?.forEach((currentNote) => {
      if (
        currentNote.noteTypeCd === NoteTypes.consultant &&
        currentNote.statusCd === NoteStatusCodes.active &&
        currentNote.actionTypeCd === NoteActionTypes.writeConsult
      ) {
        contains = true;
      }
    });
    return contains;
  },
  hasSpecialistResponded: (caseDetails: CaseDetails) => {
    let responded = false;
    const { notes } = caseDetails;
    notes?.forEach((currentNote) => {
      if (
        currentNote.noteTypeCd === NoteTypes.consultant &&
        currentNote.statusCd === NoteStatusCodes.active &&
        (currentNote.actionTypeCd === NoteActionTypes.writeConsult ||
          currentNote.actionTypeCd === NoteActionTypes.askForMoreInfo)
      ) {
        responded = true;
      }
    });
    return responded;
  },
  exportToHrm: async (caseId: string): Promise<MirthResponse> => {
    const response = await apiService.get(`cases/hrm/export/${caseId}`);
    return response.data;
  },
  getCaseDetails: async (caseId: number): Promise<CaseDetailsResponsePayloadDTO> => {
    const url = `cases/${caseId}`;
    const response = await apiService.get(url);
    return response.data;
  },
  getCaseIdByFhirId: async (fhirId: string): Promise<string | undefined> => {
    const response = await apiService.get(`cases/getcaseid/${encodeURIComponent(fhirId)}`);
    const caseId = response.data?.caseId;
    return caseId ? caseId.toString() : undefined;
  },
  isSpecialistRespondAction: (
    caseDetails: CaseDetails | undefined,
    userRole: string | undefined
  ) => {
    if (
      !caseDetails ||
      (userRole !== EconsultUserRoles.consultant &&
        userRole !== EconsultUserRoles.consultantDelegate)
    ) {
      return false;
    }
    const lastActiveNote = CaseService.getLastActiveNote(caseDetails);
    if (!lastActiveNote) {
      return false;
    }

    return (
      lastActiveNote.noteTypeCd !== NoteTypes.consultant &&
      (lastActiveNote.actionTypeCd === NoteActionTypes.send ||
        lastActiveNote.actionTypeCd === NoteActionTypes.reply ||
        lastActiveNote.actionTypeCd === NoteActionTypes.reopen ||
        lastActiveNote.actionTypeCd === NoteActionTypes.assign)
    );
  },
  getReturnNavigationUrl: (
    providerType: string | undefined,
    statusType: string | undefined,
    user: UserDTO | null
  ) => {
    const isConsultant = user ? EconsultUserService.isEconsultConsultant(user) : false;
    const isReferrer = user ? EconsultUserService.isEconsultReferrer(user) : false;
    let urlToNavigate = `/case/${providerType}/${statusType}`;
    if (providerType === 'search') {
      if (isReferrer) {
        urlToNavigate = `/case/${EconsultUserRoles.referrer}/${EconsultStatusTypes.needsAction}`;
      } else if (isConsultant || (!isConsultant && !isReferrer)) {
        urlToNavigate = `/case/${EconsultUserRoles.consultant}/${EconsultStatusTypes.needsAction}`;
      }
    }
    return urlToNavigate.toLowerCase();
  }
};

export default CaseService;
